import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, HelpCircle, LogOut, Users, Pencil, Check, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useHelp } from '../help/HelpContext';
import { ParticipantsDrawer } from '../participant/ParticipantsDrawer';
import { SteerButton } from './SteerBox';

interface Phase {
  id: string;
  name: string;
}

interface SessionTopBarProps {
  sessionId: string;
  sessionName: string;
  phases: Phase[];
  currentPhaseIndex: number;
  onPhaseChange: (index: number) => void;
  isConnected: boolean;
  playbackMode: 'browser' | 'physical' | 'both';
  onPlaybackModeChange: (mode: 'browser' | 'physical' | 'both') => void;
  onShareSession: () => void;
  onEndSession: () => void;
  sessionMode: 'active' | 'readOnly';
  projectId?: string;
  projectName?: string;
  isSecondScreenOpen?: boolean;
  onOpenSecondScreen?: () => void;
  onRename?: (newName: string) => void;
  canSteer?: boolean;
}

function getPhaseLabelClass(index: number, currentPhaseIndex: number): string {
  if (index === currentPhaseIndex) return '';
  if (index < currentPhaseIndex) return 'opacity-70';
  return 'opacity-50';
}

const SessionTopBar: React.FC<SessionTopBarProps> = ({
  sessionId,
  sessionName,
  phases,
  currentPhaseIndex,
  onPhaseChange,
  isConnected,
  onShareSession,
  onEndSession,
  sessionMode,
  projectId,
  projectName,
  isSecondScreenOpen,
  onOpenSecondScreen,
  onRename,
  canSteer,
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { openHelp } = useHelp();
  const [showParticipants, setShowParticipants] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (renaming) inputRef.current?.focus();
  }, [renaming]);

  const startRename = () => {
    if (!onRename) return;
    setRenameValue(sessionName || '');
    setRenaming(true);
  };

  const submitRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== sessionName && onRename) onRename(trimmed);
    setRenaming(false);
  };

  const userInitials = user?.displayName
    ? user.displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <>
    <header className="flex items-center justify-between whitespace-nowrap bg-navy px-6 py-3 shrink-0 h-16 shadow-md z-20">
      {/* Left: Session Identity */}
      <div className="flex items-center gap-4 text-white">
        <div
          className="size-8 flex items-center justify-center bg-white/10 rounded-lg cursor-pointer"
          onClick={() => projectId ? navigate(`/projects/${projectId}`) : navigate('/projects')}
        >
          <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
          </svg>
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            {renaming ? (
              <div className="flex items-center gap-1.5">
                <input
                  ref={inputRef}
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { submitRename(); } if (e.key === 'Escape') { setRenaming(false); } }}
                  onBlur={submitRename}
                  className="bg-white/10 text-white text-base font-bold leading-tight tracking-wide px-2 py-0.5 rounded border border-white/30 focus:outline-none focus:border-primary w-52"
                />
                <button onMouseDown={e => { e.preventDefault(); submitRename(); }} className="text-primary hover:text-primary/80"><Check size={15} /></button>
                <button onMouseDown={e => { e.preventDefault(); setRenaming(false); }} className="text-slate-400 hover:text-white"><X size={15} /></button>
              </div>
            ) : (
              <div className={`group flex items-center gap-1.5 ${onRename ? 'cursor-pointer' : ''}`} onClick={onRename ? startRename : undefined}>
                <h2 className="text-white text-base font-bold leading-tight tracking-wide">
                  {sessionName || 'Workshop Session'}
                </h2>
                {onRename && <Pencil size={13} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
              </div>
            )}
            <span className="bg-primary/20 text-primary text-[10px] px-1.5 py-0.5 rounded font-medium">
              #{sessionId.slice(0, 6)}
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-1.5 text-xs text-slate-400">
            <button onClick={() => navigate('/projects')} className="hover:text-primary transition-colors">
              Projects
            </button>
            {projectId && (
              <>
                <ChevronRight size={10} />
                <button onClick={() => navigate(`/projects/${projectId}`)} className="hover:text-primary transition-colors">
                  {projectName || 'Project'}
                </button>
              </>
            )}
            <ChevronRight size={10} />
            <span className="text-slate-300">{sessionName || 'Session'}</span>
            <span className="mx-1">·</span>
            <div className={`size-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
            <span>{isConnected ? 'Online' : 'Disconnected'}</span>
          </nav>
        </div>
      </div>

      {/* Center: Phase Timeline */}
      <div data-training-id="phase-navigator" className="hidden lg:flex items-center gap-1 flex-1 justify-center max-w-3xl px-8">
        {phases.map((phase, index) => (
          <React.Fragment key={phase.id}>
            {index > 0 && (
              <div className={`h-[1px] w-12 ${index <= currentPhaseIndex ? 'bg-primary/50' : 'bg-slate-600'}`} />
            )}
            <button
              onClick={() => onPhaseChange(index)}
              className={`flex flex-col items-center gap-1 relative cursor-pointer ${getPhaseLabelClass(index, currentPhaseIndex)}`}
            >
              {index === currentPhaseIndex && (
                <div className="absolute -top-1 size-4 rounded-full bg-primary/30 animate-pulse" />
              )}
              <div
                className={`size-2 rounded-full z-10 ${
                  index <= currentPhaseIndex
                    ? 'bg-primary'
                    : 'border border-slate-500'
                }`}
              />
              <span
                className={`text-xs font-medium whitespace-nowrap ${
                  index === currentPhaseIndex
                    ? 'text-primary font-bold'
                    : 'text-slate-300'
                }`}
              >
                {phase.name}
              </span>
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Right: Timer & Actions */}
      <div className="flex items-center gap-3">
        {canSteer && <SteerButton sessionId={sessionId} />}
        {onOpenSecondScreen && (
          <button
            onClick={onOpenSecondScreen}
            className="flex items-center justify-center gap-2 rounded-lg h-9 px-3 bg-white/5 hover:bg-white/10 text-white border border-white/10 text-sm font-medium transition-colors"
            title={isSecondScreenOpen ? 'Second screen is open' : 'Open second screen'}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
            </svg>
            {isSecondScreenOpen && (
              <div className="size-2 rounded-full bg-green-500 animate-pulse" />
            )}
          </button>
        )}
        <button
          onClick={() => setShowParticipants(true)}
          className="flex items-center justify-center gap-2 rounded-lg h-9 px-3 bg-white/5 hover:bg-white/10 text-white border border-white/10 text-sm font-medium transition-colors"
          title="Participant view"
        >
          <Users size={14} />
          <span className="hidden md:inline">Participants</span>
        </button>
        <button
          onClick={onShareSession}
          className="flex items-center justify-center rounded-lg h-9 px-3 bg-white/5 hover:bg-white/10 text-white border border-white/10 text-sm font-medium transition-colors"
        >
          Share
        </button>
        {sessionMode === 'active' && (
          <button
            onClick={onEndSession}
            className="flex cursor-pointer items-center justify-center overflow-hidden rounded-lg h-9 px-4 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-sm font-bold transition-colors"
          >
            End Session
          </button>
        )}
        <div className="w-px h-6 bg-white/10" />
        <button
          onClick={handleLogout}
          className="flex items-center justify-center rounded-lg h-9 w-9 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10 transition-colors"
          title="Sign out"
        >
          <LogOut size={16} />
        </button>
        <button
          onClick={() => openHelp()}
          className="flex items-center justify-center rounded-lg h-9 w-9 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10 transition-colors"
          title="Help"
        >
          <HelpCircle size={16} />
        </button>
        <div
          className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-[#052831] font-bold text-xs cursor-pointer hover:bg-primary/80 transition-colors border-2 border-primary/20"
          onClick={() => navigate('/profile')}
          title={user?.displayName || 'Profile'}
        >
          {userInitials}
        </div>
      </div>
    </header>
    {showParticipants && (
      <ParticipantsDrawer sessionId={sessionId} onClose={() => setShowParticipants(false)} />
    )}
  </>
  );
};

export default SessionTopBar;
